import { cn } from '@/lib/utils';
import {
  BarChart3,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Link2,
  Loader2,
  Search,
  X
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { PlotlyElement } from '@/components/Elements/Plotly';
import { Markdown } from '@/components/Markdown';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

import { Highlight } from './Highlight';
import {
  Conversation,
  CustomerRow,
  ThreadPage,
  ThreadSearch,
  ThreadSummary,
  apiAdminConversations,
  apiAdminThreads,
  apiAdminUsers,
  formatDateTime,
  timeAgo
} from './api';

// Find and read conversations, for one company or every company in the
// admin's scope. The server does the matching (every word, Arabic-folded the
// way the chat sidebar folds it) and says why each result matched; this only
// asks and shows. Read-only: nothing here touches a customer's session.

type Period = 'all' | '7' | '30' | '90' | 'custom';

const PERIODS: { id: Period; label: string }[] = [
  { id: 'all', label: 'Any time' },
  { id: '7', label: '7 days' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
  { id: 'custom', label: 'Dates…' }
];

const PAGE = 30;

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - (n - 1));
  return d.toLocaleDateString('en-CA'); // YYYY-MM-DD, local (Cairo) day
}

export interface OpenThreadRef {
  id: string;
  customer_pk: number;
}

interface Props {
  // Fixed company (its own tab), or null to search every company in scope.
  customerPk: number | null;
  customers: CustomerRow[];
  users?: { id: string; email: string }[];
  initialQuery?: string;
  initialUserId?: string;
  initialThread?: OpenThreadRef | null;
  // For the page address: a conversation can be linked and the back button
  // works.
  onStateChange?: (s: { q: string; thread: OpenThreadRef | null }) => void;
  onError?: (message: string) => void;
}

export default function ConversationBrowser({
  customerPk,
  customers,
  users,
  initialQuery = '',
  initialUserId = '',
  initialThread = null,
  onStateChange,
  onError
}: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [debounced, setDebounced] = useState(initialQuery);
  const [company, setCompany] = useState<number | null>(customerPk);
  const [companyUsers, setCompanyUsers] = useState(users ?? []);
  const [userId, setUserId] = useState(initialUserId);
  const [period, setPeriod] = useState<Period>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState<ThreadSearch['sort']>('recent');
  const [page, setPage] = useState<ThreadPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [open, setOpen] = useState<OpenThreadRef | null>(initialThread);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    onStateChange?.({ q: debounced, thread: open });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, open]);

  // The user list follows the chosen company.
  useEffect(() => {
    if (customerPk !== null) {
      setCompanyUsers(users ?? []);
      return;
    }
    setUserId('');
    if (company === null) {
      setCompanyUsers([]);
      return;
    }
    apiAdminUsers(company)
      .then((r) => setCompanyUsers(r.items))
      .catch(() => setCompanyUsers([]));
  }, [company, customerPk, users]);

  const search: ThreadSearch = useMemo(() => {
    const range =
      period === 'custom'
        ? { dateFrom: from || undefined, dateTo: to || undefined }
        : period === 'all'
          ? {}
          : { dateFrom: daysAgo(Number(period)) };
    return {
      customerPk: customerPk ?? company,
      userId: userId || undefined,
      q: debounced,
      sort,
      limit: PAGE,
      ...range
    };
  }, [customerPk, company, userId, debounced, sort, period, from, to]);

  useEffect(() => {
    const ctl = new AbortController();
    setLoading(true);
    apiAdminThreads({ ...search, offset: 0 }, ctl.signal)
      .then(setPage)
      .catch((e) => {
        if (e?.name !== 'AbortError') onError?.(e.message);
      })
      .finally(() => {
        if (!ctl.signal.aborted) setLoading(false);
      });
    return () => ctl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadMore = () => {
    if (!page) return;
    setLoadingMore(true);
    apiAdminThreads({ ...search, offset: page.offset + page.items.length })
      .then((next) =>
        setPage({
          ...next,
          offset: page.offset,
          items: [...page.items, ...next.items]
        })
      )
      .catch((e) => onError?.(e.message))
      .finally(() => setLoadingMore(false));
  };

  if (open) {
    return (
      <ThreadView
        thread={open}
        summary={page?.items.find((t) => t.id === open.id) ?? null}
        query={debounced}
        showCompany={customerPk === null}
        onBack={() => setOpen(null)}
        onError={onError}
      />
    );
  }

  const filtered =
    !!debounced.trim() || !!userId || period !== 'all' || company !== customerPk;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <div className="relative">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            dir="auto"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions and answers — e.g. اجمالي المبيعات, فاتورة, stock"
            className="pl-9 pr-9 h-10"
          />
          {query && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap text-sm">
          {customerPk === null && (
            <select
              className="h-8 rounded-md border bg-background px-2 text-sm"
              value={company ?? ''}
              onChange={(e) =>
                setCompany(e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">All companies</option>
              {customers.map((c) => (
                <option key={c.pk} value={c.pk}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <select
            className="h-8 rounded-md border bg-background px-2 text-sm disabled:opacity-50"
            value={userId}
            disabled={companyUsers.length === 0}
            onChange={(e) => setUserId(e.target.value)}
            title={
              companyUsers.length === 0 ? 'Choose a company first' : undefined
            }
          >
            <option value="">Everyone</option>
            {companyUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.email}
              </option>
            ))}
          </select>
          <div className="flex rounded-md border overflow-hidden">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={cn(
                  'px-2.5 h-8 text-xs border-r last:border-r-0',
                  period === p.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-muted-foreground'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <span className="flex items-center gap-1">
              <Input
                type="date"
                className="h-8 w-36"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <span className="text-muted-foreground text-xs">to</span>
              <Input
                type="date"
                className="h-8 w-36"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </span>
          )}
          <select
            className="h-8 rounded-md border bg-background px-2 text-sm"
            value={sort}
            onChange={(e) => setSort(e.target.value as ThreadSearch['sort'])}
          >
            <option value="recent">Latest question first</option>
            <option value="newest">Newest conversation first</option>
            <option value="questions">Most questions first</option>
          </select>
          {filtered && (
            <button
              className="text-xs text-primary hover:underline"
              onClick={() => {
                setQuery('');
                setUserId('');
                setPeriod('all');
                setCompany(customerPk);
              }}
            >
              Clear filters
            </button>
          )}
          <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1">
            {loading && <Loader2 className="size-3 animate-spin" />}
            {page
              ? `${page.total} conversation${page.total === 1 ? '' : 's'}${
                  debounced.trim() ? ' match' : ''
                }`
              : 'loading…'}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Every word must appear somewhere in the conversation: its title, a
          question or an answer. Spelling is forgiving the way the chat search
          is (أ/ا, ة/ه, ى/ي, Arabic digits). Read-only.
        </p>
      </div>

      {(page?.items ?? []).map((t) => (
        <Card
          key={t.id}
          className="p-3 cursor-pointer hover:bg-muted/40 transition-colors"
          onClick={() => setOpen({ id: t.id, customer_pk: t.customer_pk })}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div
              className="text-sm font-medium min-w-0 line-clamp-2"
              dir="auto"
            >
              <Highlight
                text={t.name || '(untitled conversation)'}
                ranges={t.title_highlights}
              />
            </div>
            <span
              className="text-xs text-muted-foreground whitespace-nowrap"
              title={formatDateTime(t.last_activity)}
            >
              {timeAgo(t.last_activity)}
            </span>
          </div>
          {t.snippet && (
            <div
              className="text-xs mt-1.5 text-muted-foreground line-clamp-2"
              dir="auto"
            >
              <span className="font-medium text-foreground/70">
                {t.snippet.kind === 'answer'
                  ? 'Answer: '
                  : debounced.trim()
                    ? 'Question: '
                    : 'Last question: '}
              </span>
              <Highlight text={t.snippet.text} ranges={t.snippet.highlights} />
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
            {customerPk === null && (
              <span className="font-medium">{t.customer_name}</span>
            )}
            <span>{t.email}</span>
            <span>
              {t.question_count}{' '}
              {t.question_count === 1 ? 'question' : 'questions'}
            </span>
            {t.has_charts && (
              <span className="flex items-center gap-1">
                <BarChart3 className="size-3" /> charts
              </span>
            )}
            <span>started {formatDateTime(t.created_at)}</span>
          </div>
        </Card>
      ))}
      {page && page.items.length === 0 && !loading && (
        <Card className="p-6 text-sm text-muted-foreground">
          {filtered
            ? 'No conversation matches. Try fewer words or a wider period.'
            : 'No conversations recorded yet.'}
        </Card>
      )}
      {page?.has_more && (
        <Button
          variant="outline"
          size="sm"
          className="self-center"
          disabled={loadingMore}
          onClick={loadMore}
        >
          {loadingMore && <Loader2 className="size-4 mr-1 animate-spin" />}
          Show more ({page.total - page.items.length} left)
        </Button>
      )}
    </div>
  );
}

function ThreadView({
  thread,
  summary,
  query,
  showCompany,
  onBack,
  onError
}: {
  thread: OpenThreadRef;
  summary: ThreadSummary | null;
  query: string;
  showCompany: boolean;
  onBack: () => void;
  onError?: (message: string) => void;
}) {
  const [items, setItems] = useState<Conversation[] | null>(null);
  const [meta, setMeta] = useState<ThreadSummary | null>(summary);
  const [current, setCurrent] = useState(0);
  const [copied, setCopied] = useState(false);
  const refs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    apiAdminConversations(thread.customer_pk, {
      threadId: thread.id,
      q: query
    })
      .then((r) => !cancelled && setItems(r.items))
      .catch((e) => onError?.(e.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.id, thread.customer_pk, query]);

  // Opened from a link: the title and the person are not in the list yet.
  useEffect(() => {
    if (meta) return;
    apiAdminThreads({
      customerPk: thread.customer_pk,
      threadId: thread.id,
      limit: 1
    })
      .then((r) => r.items[0] && setMeta(r.items[0]))
      .catch(() => undefined);
  }, [thread.id, thread.customer_pk, meta]);

  const matches = useMemo(
    () => (items ?? []).filter((c) => c.match).map((c) => c.id),
    [items]
  );

  const jump = (i: number) => {
    if (matches.length === 0) return;
    const n = (i + matches.length) % matches.length;
    setCurrent(n);
    refs.current[matches[n]]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Straight to the first match once the conversation is in.
  useEffect(() => {
    if (matches.length > 0) {
      setTimeout(() => jump(0), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches.length]);

  const copyLink = () => {
    const params = new URLSearchParams({
      view: 'conversations',
      customer: String(thread.customer_pk),
      thread: thread.id
    });
    if (query.trim()) params.set('q', query.trim());
    navigator.clipboard
      ?.writeText(`${window.location.origin}/admin?${params.toString()}`)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };

  let questionNo = 0;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap sticky top-0 z-10 bg-background/95 backdrop-blur py-1">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ChevronLeft className="size-4 mr-1" /> All conversations
        </Button>
        <span className="text-sm font-medium min-w-0 truncate" dir="auto">
          {meta?.name}
        </span>
        <span className="ml-auto flex items-center gap-1">
          {query.trim() && (
            <>
              <span className="text-xs text-muted-foreground mr-1">
                {matches.length
                  ? `match ${current + 1} of ${matches.length}`
                  : 'no question here has the words'}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="size-7"
                disabled={matches.length < 2}
                onClick={() => jump(current - 1)}
                aria-label="Previous match"
              >
                <ChevronUp className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-7"
                disabled={matches.length < 2}
                onClick={() => jump(current + 1)}
                aria-label="Next match"
              >
                <ChevronDown className="size-4" />
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" className="h-7" onClick={copyLink}>
            <Link2 className="size-3.5 mr-1" />
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </span>
      </div>
      {meta && (
        <div className="text-xs text-muted-foreground">
          {showCompany && `${meta.customer_name} · `}
          {meta.email} · started {formatDateTime(meta.created_at)} ·{' '}
          {meta.question_count}{' '}
          {meta.question_count === 1 ? 'question' : 'questions'}
        </div>
      )}
      {items === null && (
        <Card className="p-6 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="size-4 animate-spin" /> Loading the conversation…
        </Card>
      )}
      {items?.length === 0 && (
        <Card className="p-6 text-sm text-muted-foreground">
          This conversation has no questions.
        </Card>
      )}
      {(items ?? []).map((c) => {
        if (c.kind === 'notice')
          return (
            <div
              key={c.id}
              className="text-xs text-muted-foreground italic px-3 py-2 border-l-2 border-muted"
              dir="auto"
            >
              {c.question} · {formatDateTime(c.asked_at)}
            </div>
          );
        questionNo += 1;
        const isCurrent = matches[current] === c.id;
        return (
          <Card
            key={c.id}
            ref={(el) => (refs.current[c.id] = el)}
            className={cn(
              'overflow-hidden scroll-mt-14',
              c.match && 'ring-1 ring-amber-400/70',
              isCurrent && 'ring-2 ring-amber-500'
            )}
          >
            <div className="px-4 py-2 bg-muted/40 flex items-center justify-between gap-3 flex-wrap text-xs text-muted-foreground">
              <span>
                Question {questionNo}
                {c.match && (
                  <span className="ml-2 rounded-full bg-amber-200/70 dark:bg-amber-500/30 text-foreground px-1.5 py-0.5">
                    matches
                  </span>
                )}
              </span>
              <span>{formatDateTime(c.asked_at)}</span>
            </div>
            <div className="p-4 flex flex-col gap-3">
              <div
                className="text-sm font-semibold leading-relaxed"
                dir="auto"
              >
                <Highlight text={c.question} ranges={c.question_highlights} />
              </div>
              {c.answer && (
                <div
                  className="text-sm border-l-2 border-primary/40 pl-3"
                  dir="auto"
                >
                  <Markdown>{c.answer}</Markdown>
                </div>
              )}
              {c.elements.length > 0 && (
                <div className="flex flex-col gap-3">
                  {c.elements.map((el, i) =>
                    el.type === 'plotly' && el.url ? (
                      <div
                        key={i}
                        className="rounded-lg border p-2 bg-background"
                      >
                        <PlotlyElement
                          element={
                            {
                              id: `${c.id}-el-${i}`,
                              type: 'plotly',
                              name: el.name ?? 'chart',
                              display: 'inline',
                              url: el.url
                            } as any
                          }
                        />
                      </div>
                    ) : el.url ? (
                      <img
                        key={i}
                        src={el.url}
                        alt={el.name ?? 'chart'}
                        className="rounded-lg border max-w-full"
                      />
                    ) : null
                  )}
                </div>
              )}
              {c.python_code && (
                <details>
                  <summary className="text-xs text-muted-foreground cursor-pointer">
                    Code that produced this answer
                  </summary>
                  <pre className="text-[11px] mt-2 overflow-x-auto bg-muted/50 rounded p-2 leading-relaxed">
                    {c.python_code}
                  </pre>
                </details>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
